import type {
  CotProperty,
  CotTask,
  Incident,
  ObjectId,
  PropertyRef,
  Ref,
  Track,
  WorkOrder,
} from '@/api/types'
import { refId, toRef } from './refs'
import { progressOf, resolveState, UNKNOWN_STATE } from './track'

const INCIDENT_EXT = 'c_incidentactive'
const WORK_ORDER_EXT = 'c_work_order'

type Props = Map<ObjectId, CotProperty>

const ext = (task: CotTask, key: string): Record<string, unknown> =>
  (task.extensions?.[key] as Record<string, unknown> | undefined) ?? {}

/** Los arreglos de la extensión pueden traer ids o propiedades ya resueltas. */
function idsFrom(value: unknown): ObjectId[] {
  if (!Array.isArray(value)) return []
  const out: ObjectId[] = []
  for (const v of value) {
    if (typeof v === 'string') out.push(v)
    else if (v && typeof v === 'object') {
      const id = (v as { _id?: unknown })._id
      if (typeof id === 'string') out.push(id)
    }
  }
  return out
}

/** Estado de la tarea: `status` apunta a la propiedad del catálogo. */
function stateOf(task: CotTask, track: Track, props: Props) {
  const statusId = refId(task.status ?? null)
  let code: string | null = null

  if (statusId) {
    code = track.byPropertyId[statusId] ?? null
    if (!code) {
      // Si el estado no está indexado (legacy dado de baja, que el catálogo no
      // devuelve), se intenta con el código de la propia propiedad.
      const p = props.get(statusId)
      const n = p?.name
      if (n && typeof n === 'object' && n.code) code = n.code
    }
  }

  const state = resolveState(code, track)
  return { state, progress: progressOf(state.step, track) }
}

/** Ids de propiedad que una incidencia necesita resueltos para pintarse. */
export function incidentPropertyIds(task: CotTask): ObjectId[] {
  const e = ext(task, INCIDENT_EXT)
  const refs: PropertyRef[] = [
    task.status ?? null,
    task.status1 ?? null,
    task.status2 ?? null,
    task.status3 ?? null,
    task.status4 ?? null,
    (e['priority'] as PropertyRef) ?? null,
    (e['incident_asset'] as PropertyRef) ?? null,
    (e['incident_ut'] as PropertyRef) ?? null,
  ]
  return refs.map(refId).filter((id): id is ObjectId => Boolean(id))
}

export function toIncident(task: CotTask, track: Track, props: Props): Incident {
  const e = ext(task, INCIDENT_EXT)
  const { state, progress } = stateOf(task, track, props)

  const active = idsFrom(e['incident_ots'])
  const finished = idsFrom(e['incident_finished_ots'])
  // Hay que leer los DOS arreglos: al cerrarse una incidencia sus órdenes
  // migran del primero al segundo, y una incidencia abierta puede tener
  // órdenes en cualquiera de los dos (corrección §2.3).
  const otPropertyIds = [...new Set([...active, ...finished])]

  return {
    _id: task._id,
    serial: task.serial ?? null,
    ticket: task.serial ? `INC-${task.serial}` : task._id.slice(-6).toUpperCase(),
    name: task.name ?? 'Incidencia sin nombre',
    channel: task.channel ?? null,
    createdAt: task.createdAt ?? null,
    station: toRef(task.status1 ?? null, props),
    area: toRef(task.status2 ?? null, props),
    assetFamily: toRef(task.status3 ?? null, props),
    activity: toRef(task.status4 ?? null, props),
    asset: toRef((e['incident_asset'] as PropertyRef) ?? null, props),
    ut: toRef((e['incident_ut'] as PropertyRef) ?? null, props),
    priority: toRef((e['priority'] as PropertyRef) ?? null, props),
    state,
    progress,
    // Sale del largo de los arreglos, sin llamadas (corrección §2.4). Incluye
    // órdenes dadas de baja, así que puede superar a las filas desplegadas.
    otCount: otPropertyIds.length,
    otPropertyIds,
  }
}

/** Ids que una OT necesita resueltos: el proveedor y los refs de su extensión. */
export function workOrderPropertyIds(task: CotTask | null, property: CotProperty): ObjectId[] {
  const e = task ? ext(task, WORK_ORDER_EXT) : {}
  const s = (property.schemaInstance ?? {}) as Record<string, unknown>
  const refs: PropertyRef[] = [
    task?.status ?? null,
    task?.status5 ?? null,
    (e['ot_contract'] as PropertyRef) ?? (s['ot_contract'] as PropertyRef) ?? null,
    (e['ot_activity'] as PropertyRef) ?? (s['ot_activity'] as PropertyRef) ?? null,
    (s['ot_provider'] as PropertyRef) ?? null,
  ]
  return refs.map(refId).filter((id): id is ObjectId => Boolean(id))
}

const HEX24 = /^[0-9a-fA-F]{24}$/

/** Códigos (no ids) que una OT necesita resueltos: hoy, el proveedor. */
export function workOrderPropertyCodes(task: CotTask | null, property: CotProperty): string[] {
  if (task?.status5) return []
  const raw = (property.schemaInstance as Record<string, unknown> | undefined)?.['ot_provider']
  return typeof raw === 'string' && raw && !HEX24.test(raw) ? [raw] : []
}

/**
 * El proveedor puede llegar de dos lados: `status5` de la tarea (un ObjectId)
 * o `ot_provider` de la propiedad, que es un **código** (`"p1110033"`) y no un
 * id. Cuando solo hay código se muestra tal cual, que es más útil que un
 * guion.
 */
function providerRef(
  task: CotTask | null,
  s: Record<string, unknown>,
  props: Props,
  codes?: Map<string, CotProperty>,
): Ref {
  if (task?.status5) return toRef(task.status5, props)
  const raw = s['ot_provider']
  if (typeof raw === 'string' && raw) {
    if (HEX24.test(raw)) return toRef(raw, props)
    const hit = codes?.get(raw)
    const n = hit?.name
    const display = typeof n === 'string' ? n : n?.display
    // Si no se pudo resolver, el código es mejor que un guion.
    return { _id: hit?._id ?? null, code: raw, display: display ?? raw }
  }
  return toRef((raw as PropertyRef) ?? null, props)
}

/** "4222 -3037 - ee_s179 - …" → 4222 */
function serialFromDisplay(property: CotProperty): number | null {
  const n = property.name
  const display = typeof n === 'string' ? n : (n?.display ?? '')
  const m = /^\s*(\d+)/.exec(display)
  return m?.[1] ? Number(m[1]) : null
}

/**
 * `task` puede ser null si el backend tampoco pudo traerla (credencial vencida
 * o mal configurada). En ese caso la orden se arma solo con su propiedad —
 * proveedor, actividad, contrato y solución salen de ahí — sin estado ni
 * fechas.
 */
export function toWorkOrder(
  task: CotTask | null,
  property: CotProperty,
  track: Track,
  props: Props,
  codes?: Map<string, CotProperty>,
  userCanOpen = true,
): WorkOrder {
  const e = task ? ext(task, WORK_ORDER_EXT) : {}
  const s = (property.schemaInstance ?? {}) as Record<string, unknown>
  const pick = (key: string): unknown => e[key] ?? s[key]

  const { state, progress } = task
    ? stateOf(task, track, props)
    : { state: UNKNOWN_STATE, progress: progressOf(null, track) }

  const solution = pick('ot_solution')
  const displayName =
    task?.name ??
    (typeof property.name === 'string' ? property.name : property.name?.display) ??
    'Orden sin nombre'

  return {
    _id: task?._id ?? property._id,
    userCanOpen: userCanOpen && task !== null,
    serial: task?.serial ?? serialFromDisplay(property),
    name: displayName,
    channel: task?.channel ?? null,
    startDate: task?.startDate ?? null,
    endDate: task?.endDate ?? null,
    provider: providerRef(task, s, props, codes),
    contract: toRef((pick('ot_contract') as PropertyRef) ?? null, props),
    activity: toRef((pick('ot_activity') as PropertyRef) ?? null, props),
    solution: typeof solution === 'string' && solution.trim() ? solution : null,
    state,
    progress,
  }
}
